// Certified Materials — pure RPC layer (ported from old
// `views/certified-materials/api/materials-api.ts`).
//
// All calls hit the dedicated certified-materials service and go through the
// shared `apiFetch` wrapper (auth headers, error handling, 401 redirect).

import {
  API_BASE_URL,
  CERTIFIED_MATERIALS_SERVICE_URL,
} from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  IApiDocument,
  IApiEnvelope,
  IApiFieldMatch,
  IApiOrder,
  IApiOrdersResponse,
  IAssignee,
  IConfirmDocumentsReviewBody,
  IConfirmEvidenceResponse,
  IEvidenceItem,
  IFilterOptionRaw,
  IImportOrdersBody,
  ISendRemindersBody,
  ISendRequestEntry,
  IUpdateDocumentBody,
  IUpdateOrderBody,
  IUploadDocBody,
  IUploadOrderDocBody,
} from './types';

const BASE = CERTIFIED_MATERIALS_SERVICE_URL;

// NOTE on response shapes (verified against old `@lib/fetch` usage):
//  - list endpoint body is `{ data: [...], pagination }` (IApiOrdersResponse) — NOT
//    further wrapped.
//  - detail / update endpoints return the order object directly.
//  - filter-options endpoints ARE wrapped: `{ data: [...] }` (IApiEnvelope).
// The shared `apiFetch` returns the raw body (unlike the old fetch wrapper that
// added a `{ data, error, status }` envelope), so we type each call to its real
// body shape.

export function fetchOrdersList(qs: string): Promise<IApiOrdersResponse> {
  return apiFetch<IApiOrdersResponse>(`/orders${qs}`, {}, BASE);
}

export function fetchOrderById(orderId: string): Promise<IApiOrder> {
  return apiFetch<IApiOrder>(`/orders/${orderId}`, {}, BASE);
}

export function updateOrder(
  orderId: string,
  body: IUpdateOrderBody,
): Promise<IApiOrder> {
  return apiFetch<IApiOrder>(
    `/orders/${orderId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BASE,
  );
}

export function updateOrderStatus(
  orderId: string,
  status: string,
): Promise<IApiOrder> {
  return apiFetch<IApiOrder>(
    `/orders/${orderId}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    BASE,
  );
}

// Imports a PO spreadsheet. The cert-materials service parses + extracts the
// rows server-side; the client just hands over the uploaded file reference.
export function importOrders(body: IImportOrdersBody): Promise<unknown> {
  return apiFetch<unknown>(
    '/orders',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

// Uploads an evidence document. Server classifies + matches it to orders and
// starts AI processing. Returns 409 CONFLICT for duplicate files.
export function uploadDocument(body: IUploadDocBody): Promise<unknown> {
  return apiFetch<unknown>(
    '/documents',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

// Dedup check: returns any documents already stored with this MD5 hash.
// `qs` must be a pre-built query string, e.g. `?filter=...` (built in the hook layer).
export function fetchDocumentsByMd5Hash(
  qs: string,
): Promise<{ data: IApiDocument[] }> {
  return apiFetch<{ data: IApiDocument[] }>(`/documents${qs}`, {}, BASE);
}

// Documents awaiting human review (drives the Review Docs button count AND the
// list rendered inside the review dialog).
// `qs` must be a pre-built query string (built in the hook layer).
export function fetchDocumentsPendingReview(
  qs: string,
): Promise<{ data: IApiDocument[] }> {
  return apiFetch<{ data: IApiDocument[] }>(`/documents${qs}`, {}, BASE);
}

// Orders available to link a document to (review dialog order picker).
export function fetchAvailableOrders(limit = 100): Promise<{
  data: IApiOrder[];
}> {
  return apiFetch<{ data: IApiOrder[] }>(`/orders?limit=${limit}`, {}, BASE);
}

// Confirms reviewed documents (type + linked orders + extraction edits).
export function confirmDocumentsReview(
  body: IConfirmDocumentsReviewBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/documents/confirm',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

// Bulk "send request" — one document_submission request per supplier. The
// service takes the entries array directly as the body.
export function sendBulkRequests(
  entries: ISendRequestEntry[],
): Promise<unknown> {
  return apiFetch<unknown>(
    '/requests',
    { method: 'POST', body: JSON.stringify(entries) },
    BASE,
  );
}

// Bulk "send reminder" — one reminder per pending request id.
export function sendBulkReminders(body: ISendRemindersBody): Promise<unknown> {
  return apiFetch<unknown>(
    '/requests/remind',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

export function deleteOrders(ids: string[]): Promise<void> {
  return apiFetch<void>(
    '/orders',
    { method: 'DELETE', body: JSON.stringify({ ids }) },
    BASE,
  );
}

// ── Filter options ──────────────────────────────────────────────────────────

function fetchFilterOptions(
  path: string,
): Promise<IApiEnvelope<IFilterOptionRaw[]>> {
  return apiFetch<IApiEnvelope<IFilterOptionRaw[]>>(
    `/filter-options/${path}`,
    {},
    BASE,
  );
}

// ── Order-details drawer endpoints ──────────────────────────────────────────

// Field-match payload is returned as a bare array (no envelope).
export function fetchOrderDocumentCheck(
  orderId: string,
): Promise<IApiFieldMatch[]> {
  return apiFetch<IApiFieldMatch[]>(
    `/orders/${orderId}/field-matches`,
    {},
    BASE,
  );
}

export function fetchOrderEvidenceItems(
  orderId: string,
): Promise<IEvidenceItem[]> {
  return apiFetch<IEvidenceItem[]>(
    `/orders/${orderId}/evidence-items`,
    {},
    BASE,
  );
}

export function confirmEvidence(
  orderId: string,
  evidenceId: string,
  confirmed: boolean,
): Promise<IConfirmEvidenceResponse> {
  return apiFetch<IConfirmEvidenceResponse>(
    `/orders/${orderId}/evidence-items/${evidenceId}/confirm`,
    { method: 'PATCH', body: JSON.stringify({ confirmed }) },
    BASE,
  );
}

export function uploadOrderDocument(
  orderId: string,
  body: IUploadOrderDocBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/orders/${orderId}/documents`,
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

export function deleteDocument(documentId: string): Promise<void> {
  return apiFetch<void>(`/documents/${documentId}`, { method: 'DELETE' }, BASE);
}

export function fetchDocumentById(documentId: string): Promise<IApiDocument> {
  return apiFetch<IApiDocument>(`/documents/${documentId}`, {}, BASE);
}

// Update a document's extracted-data certificate block (PATCH /documents/:id).
// `code` is the certificate identifier (tcNumber / invoiceNumber / shippingDocNumber).
export function updateDocument(
  documentId: string,
  body: IUpdateDocumentBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/documents/${documentId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BASE,
  );
}

export function requestDocumentReupload(
  documentId: string,
  message: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/requests',
    {
      method: 'POST',
      body: JSON.stringify([
        { type: 'document_revision', documentIds: [documentId], message },
      ]),
    },
    BASE,
  );
}

export function validateFieldMatch(
  matchId: string,
  fieldName: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/field-matches/${matchId}/manual-validations`,
    { method: 'POST', body: JSON.stringify({ fieldName }) },
    BASE,
  );
}

export function revokeFieldMatchValidation(
  matchId: string,
  fieldName: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/field-matches/${matchId}/manual-validations/${encodeURIComponent(fieldName)}`,
    { method: 'PATCH', body: JSON.stringify({ action: 'revoke' }) },
    BASE,
  );
}

export function fetchUsersByCompany(
  companyId: string,
): Promise<IApiEnvelope<IAssignee[]> | IAssignee[]> {
  return apiFetch<IApiEnvelope<IAssignee[]> | IAssignee[]>(
    `/users?companyId=${encodeURIComponent(companyId)}&limit=100`,
    {},
    BASE,
  );
}

// Platform standards (GOTS, GRS, ...) — the list used for order-line standards,
// from the main api-service (NOT the certified-materials filter-options).
export function fetchPlatformStandards(): Promise<{
  data: Array<{ id: string; name: string; shortName?: string | null }>;
}> {
  return apiFetch('/v3/standards?count=100', {}, API_BASE_URL);
}

export const fetchStandardsOptions = () => fetchFilterOptions('standards');
export const fetchVendorsOptions = () => fetchFilterOptions('vendors');
export const fetchSeasonsOptions = () => fetchFilterOptions('seasons');
export const fetchCustomTagsOptions = () => fetchFilterOptions('custom-tags');
export const fetchAssigneesOptions = () => fetchFilterOptions('assignees');
export const fetchBrandsOptions = () => fetchFilterOptions('brands');
export const fetchResponsiblePersonsOptions = () =>
  fetchFilterOptions('responsible-persons');
